import {useState, useEffect, useRef, useCallback} from 'react';
import {
  LiveAnswersClient,
  LiveAnswersMessage,
  LiveAnswersStreamingCompletionData,
  StripeAssistantLiveAnswersSource,
} from '@stripe-internal/conversations-client';
import {view} from '@sail/ui';
import {useAnalytics} from '../../AnalyticsProvider';
import {
  InlineLinkClickHandler,
  InlineLinkProps,
} from '../../StripeAssistant/LiveAnswersV2/InlineLinkClickHandler';
import {ChatBubbleDotCom} from './ChatBubbleDotCom';
import {TextBody} from './TextBody';
import {useWordChunkStreamer} from '../DotCom/useWordChunkStreamer';
import {TypingRowDotCom} from './TypingRowDotCom';
import {useDotComFeedbackButtons} from '../../FeatureFlagsProvider';
import {DotComSourcesDropdown} from '../DotCom/Sources/DotComSourcesDropdown';
import {FeedbackButtons} from '../DotCom/FeedbackButtons';
import {useLiveAnswersPoller} from '../../helpers/useLiveAnswersPoller';

type Props = {
  liveAnswersMessage: LiveAnswersMessage;
  liveAnswersClient: LiveAnswersClient;
  onDoneStreaming: (_onDoneData: LiveAnswersStreamingCompletionData) => void;
  onError: (error?: string) => void;
};

export const inlineLinkClickedActionEvent =
  'universal_help_live_answers_frontend_inline_link_clicked';

function DotComStreamingOutput({
  textStream,
  messageData,
  sources = [],
  doneStreaming = false,
  disableAnimation = false,
}: {
  textStream: string;
  messageData: NonNullable<LiveAnswersMessage['data']>;
  sources: StripeAssistantLiveAnswersSource[];
  doneStreaming?: boolean;
  disableAnimation?: boolean;
}) {
  const analytics = useAnalytics();
  const feedbackButtons = useDotComFeedbackButtons();

  const completedSuccessfully =
    messageData.is_complete &&
    !messageData.was_halted &&
    !messageData.not_answerable;
  const showSources =
    completedSuccessfully && doneStreaming && sources.length > 0;

  const getBubbleAnimation = (index: number) => {
    if (disableAnimation) {
      return 'disabled';
    }

    return index === 0 ? 'skip-scale' : 'all';
  };

  const rows = textStream
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line, i) => (
      <ChatBubbleDotCom
        // eslint-disable-next-line react/no-array-index-key
        key={`live-answers-response-${i}`}
        animation={getBubbleAnimation(i)}
        background="neutral100"
      >
        <InlineLinkClickHandler
          onClick={({href, innerText, parentInnerText}: InlineLinkProps) => {
            analytics.action(inlineLinkClickedActionEvent, {
              live_answers_source_url: href,
              live_answers_source_title: innerText,
              live_answers_source_context: parentInnerText,
            });
          }}
        >
          <TextBody body={line} background="neutral100" />
        </InlineLinkClickHandler>
      </ChatBubbleDotCom>
    ));

  return (
    <view.div
      css={{
        stack: 'y',
        gap: 'small',
        width: '100%',
      }}
    >
      {rows}
      {showSources ? (
        <view.div
          css={{
            stack: 'x',
            gap: 'small',
            width: '100%',
          }}
        >
          <view.div
            css={{
              width: 'calc(100% - 60px)',
            }}
          >
            <DotComSourcesDropdown sources={sources} />
          </view.div>
          {feedbackButtons ? (
            <FeedbackButtons streamingConversationId={messageData.streamId} />
          ) : null}
        </view.div>
      ) : null}
    </view.div>
  );
}

export function LiveAnswersRowDotCom({
  liveAnswersMessage,
  liveAnswersClient,
  onDoneStreaming,
  onError,
}: Props) {
  const alreadyCompleted =
    !!liveAnswersMessage.data?.is_complete ||
    !!liveAnswersMessage.data?.was_halted ||
    !!liveAnswersMessage.data?.not_answerable;
  const [doneStreaming, setDoneStreaming] = useState<boolean>(alreadyCompleted);
  const onDoneStreamingRef = useRef(onDoneStreaming);
  const onErrorRef = useRef(onError);
  const hasTriggeredCompletionRef = useRef(false);

  const {poller, isCancelled, isComplete, error, message} =
    useLiveAnswersPoller(liveAnswersClient, liveAnswersMessage);
  const messageData = message.data;

  useEffect(() => {
    onDoneStreamingRef.current = onDoneStreaming;
  }, [onDoneStreaming]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    poller.startPolling();
    return () => poller.stopPolling();
  }, [poller]);

  const onFinishedStreaming = useCallback(() => {
    if (alreadyCompleted || hasTriggeredCompletionRef.current || !isComplete) {
      return;
    }

    hasTriggeredCompletionRef.current = true;
    setDoneStreaming(true);

    if (isCancelled) {
      onDoneStreamingRef.current({content: ''});
      return;
    }

    onDoneStreamingRef.current({
      content: messageData?.content || '',
      answerType: messageData?.not_answerable ? 'unanswerable' : 'answerable',
      sources: message.sources,
    });
  }, [alreadyCompleted, isCancelled, isComplete, message.sources, messageData]);

  const {textStream, setText} = useWordChunkStreamer({
    initialText: messageData?.content || '',
    disabled: doneStreaming,
    onFinished: onFinishedStreaming,
  });

  useEffect(() => {
    setText(messageData?.content || '');
  }, [messageData?.content, setText]);

  useEffect(() => {
    if (
      !alreadyCompleted &&
      isCancelled &&
      !hasTriggeredCompletionRef.current
    ) {
      hasTriggeredCompletionRef.current = true;
      setDoneStreaming(true);
      onDoneStreamingRef.current({content: ''});
    }
  }, [alreadyCompleted, isCancelled]);

  useEffect(() => {
    if (error) {
      onErrorRef.current(error);
    }
  }, [error]);

  const showSources =
    doneStreaming &&
    !isCancelled &&
    !messageData?.not_answerable &&
    message.sources.length > 0;
  const trimmedContent = textStream.trim();

  if (isComplete && trimmedContent === '') {
    return null;
  }

  if (error) {
    return null;
  }

  if (!messageData || trimmedContent === '') {
    return <TypingRowDotCom animateIn={false} />;
  }

  const disableAnimation = alreadyCompleted || isCancelled;

  return (
    <DotComStreamingOutput
      textStream={trimmedContent}
      messageData={messageData}
      sources={showSources ? message.sources : []}
      doneStreaming={doneStreaming}
      disableAnimation={disableAnimation}
    />
  );
}
